import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map, distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export type ActorKey = 'cot' | 'uvm' | 'pua' | 'bm' | 'ward' | 'mmg';

export interface Actor {
  id?: number | string;
  code: string;
  name: string;
  aslId?: number;
  [k: string]: any;
}

export interface UvmType extends Actor {
  category?: { id: number; code: string; name: string } | null;
}

export interface AslDetails {
  id: number;
  code: string;
  name: string;
  cots?: Actor[];
  wards?: Actor[];
  uvmTypes?: UvmType[];
  puas?: Actor[];
  mmgs?: Actor[];
  bedManagers?: Actor[];
}

interface ContextSlot<T extends Actor = Actor> {
  label: string;
  items: T[];
  selected: T | null;
}

export type ContextState = Record<ActorKey, ContextSlot>;

const LABELS: Record<ActorKey, string> = {
  cot: 'COT',
  uvm: 'UVM',
  pua: 'PUA',
  bm: 'BM',
  ward: 'REP',
  mmg: 'MMG',
};

const KC_ATTR_NAME: Record<ActorKey, string> = {
  cot: 'COT',
  uvm: 'UVM',
  pua: 'PUA',
  bm: 'BM',
  ward: 'REP',
  mmg: 'MMG',
};

@Injectable({ providedIn: 'root' })
export class ContextService {
  private readonly keys: ActorKey[] = ['cot', 'uvm', 'pua', 'bm', 'ward', 'mmg'];
  private readonly order: ActorKey[] = ['cot', 'uvm', 'pua', 'bm', 'ward', 'mmg'];
  public patientFiscalCode: string | undefined;

  private readonly _state = new BehaviorSubject<ContextState>(this.createInitialState());
  readonly state$ = this._state.asObservable();

  private allowCodes: Record<ActorKey, Set<string>> = {
    cot: new Set(), uvm: new Set(), pua: new Set(), bm: new Set(), ward: new Set(), mmg: new Set()
  };
  private allowIds: Record<ActorKey, Set<string>> = {
    cot: new Set(), uvm: new Set(), pua: new Set(), bm: new Set(), ward: new Set(), mmg: new Set()
  };

  private allowReady = false;
  private rawReady = false;

  private raw: Record<ActorKey, Actor[]> = {
    cot: [], uvm: [], pua: [], bm: [], ward: [], mmg: []
  };

  constructor(private http: HttpClient) {
    const s = this._state.value;
    this.keys.forEach((k) => {
      const raw = sessionStorage.getItem(this.storageKey(k));
      if (raw) { try { s[k].selected = JSON.parse(raw); } catch { s[k].selected = null; } }
    });
    this._state.next({ ...s });
    this.normalizeSingleSelection();
  }

  updateAllowFromAttributes(attrs: Record<string, any> | null | undefined): void {
    this.keys.forEach((k) => { this.allowCodes[k].clear(); this.allowIds[k].clear(); });

    if (attrs && typeof attrs === 'object') {
      this.keys.forEach((key) => {
        const raw = (attrs as any)[KC_ATTR_NAME[key]];
        const values: string[] = Array.isArray(raw)
          ? raw.flatMap((v) => typeof v === 'string' ? this.splitAttr(v) : [String(v)])
          : typeof raw === 'string'
            ? this.splitAttr(raw)
            : [];

        values.forEach((v) => {
          const x = v.trim();
          if (!x) return;
          if (/^\d+$/.test(x)) this.allowIds[key].add(x);
          else this.allowCodes[key].add(x.toUpperCase());
        });
      });
    }

    this.allowReady = true;
    this.refreshFilteredFromRaw();
  }

  snapshot(): ContextState { return this._state.value; }

  private setAvailable(key: ActorKey, items: Actor[]): void {
    const s = this.snapshot();
    s[key] = { ...s[key], items: items ?? [] };

    const sel = s[key].selected;
    const found = sel ? s[key].items.some((it) => it.code === sel.code) : false;

    if (this.allowReady && this.rawReady && sel && !found) {
      s[key] = { ...s[key], selected: null };
      sessionStorage.removeItem(this.storageKey(key));
    }

    this._state.next({ ...s });
  }

  select(key: ActorKey, item: Actor | null): void {
    const s = this.snapshot();

    if (item) {
      this.clearOtherSelections(key, s);
      s[key] = { ...s[key], selected: item };
      sessionStorage.setItem(this.storageKey(key), JSON.stringify(item));
    } else {
      s[key] = { ...s[key], selected: null };
      sessionStorage.removeItem(this.storageKey(key));
    }

    this._state.next({ ...s });
  }

  getSelected(key: ActorKey): Actor | null { return this.snapshot()[key]?.selected ?? null; }

  getHeaderValues(): { actorCode: string; actorReferenceCode: string } | null {
    const s = this.snapshot();
    for (const k of this.order) {
      const sel = s[k].selected;
      if (sel?.code) return { actorCode: String(sel.code), actorReferenceCode: LABELS[k] };
    }
    return null;
  }

  getRole() {
    const s = this.snapshot();
    for (const k of this.order) {
      const sel = s[k].selected;
      if (sel?.code) {
        switch (LABELS[k]) {
          case 'COT':
            return 'OPERATORE_COT';
          case 'UVM':
            const cat = sel['category'];
            if(cat['code'] === 'UVM') {
              return 'OPERATORE_UVM';
            } else {
              return 'OPERATORE_UVCP';
            }
          case 'PUA':
            return 'OPERATORE_PUA';
          case 'BM':
            return 'OPERATORE_BM';
          case 'REP':
            return 'OPERATORE_SEGNALANTE';
          case 'MMG':
            return 'OPERATORE_MMG';
        }
      }
    }
    return null;
  }

  bootstrapFromAslDetails(aslCode?: string): Observable<AslDetails | null> {
    const url = aslCode
      ? `${environment.beServiceUrl}/v1/asl/find-details?code=${encodeURIComponent(aslCode)}`
      : `${environment.beServiceUrl}/v1/asl/find-details`;

    return this.http.get<AslDetails[]>(url).pipe(
      map((list) => {
        const picked = (aslCode ? list.find((x) => x.code === aslCode) : list[0]) ?? null;
        if (picked) this.applyAslDetails(picked);
        return picked;
      }),
      catchError(() => {
        this.raw = { cot: [], uvm: [], pua: [], bm: [], ward: [], mmg: [] };
        this.rawReady = true;
        this.refreshFilteredFromRaw();
        return of(null);
      })
    );
  }

  private storageKey(k: ActorKey) { return `selected:${k}`; }

  private createInitialState(): ContextState {
    const base = (k: ActorKey): ContextSlot => ({ label: LABELS[k], items: [], selected: null });
    return { cot: base('cot'), uvm: base('uvm'), pua: base('pua'), bm: base('bm'), ward: base('ward'), mmg: base('mmg') };
  }

  private applyAslDetails(asl: AslDetails) {
    const onlyActors = (arr?: Actor[]) => Array.isArray(arr) ? arr.map((a) => ({ ...a })) : [];
    this.raw.cot  = onlyActors(asl.cots);
    this.raw.ward = onlyActors(asl.wards);
    this.raw.uvm  = onlyActors(asl.uvmTypes);
    this.raw.pua  = onlyActors(asl.puas);
    this.raw.bm   = onlyActors(asl.bedManagers);
    this.raw.mmg = onlyActors(asl.mmgs)

    this.rawReady = true;
    this.refreshFilteredFromRaw();
  }

  private refreshFilteredFromRaw(): void {
    if (!this.allowReady || !this.rawReady) return;
    const s = this.snapshot();
    // const filtered: Record<ActorKey, Actor[]> = {
    //   cot: this.filterByAllowList(this.raw.cot, 'cot'),
    //   uvm: this.filterByAllowList(this.raw.uvm, 'uvm'),
    //   pua: this.filterByAllowList(this.raw.pua, 'pua'),
    //   bm:  this.filterByAllowList(this.raw.bm, 'bm'),
    //   ward: this.filterByAllowList(this.raw.ward, 'ward'),
    //   mmg: this.filterByAllowList(this.raw.mmg, 'mmg'),
    // };

    // this.keys.forEach((k) => this.setAvailable(k, filtered[k]));

      this.keys.forEach((k) => {
    const items = this.filterByAllowList(this.raw[k], k);
    const sel = s[k].selected;
    const found = sel ? items.some((it) => it.code === sel.code) : false;

    s[k] = {
      ...s[k],
      items,
      selected: sel && !found ? null : sel,
    };

    if (sel && !found) {
      sessionStorage.removeItem(this.storageKey(k));
    }
  });

  this._state.next({ ...s }); // una sola emissione
  }

  private filterByAllowList<T extends Actor>(items: T[], key: ActorKey): T[] {
    if (!this.allowReady) return [];
    const codes = this.allowCodes[key];
    const ids = this.allowIds[key];
    if (codes.size === 0 && ids.size === 0) return [];
    return items.filter((it) => {
      const code = String(it.code || '').toUpperCase();
      const id = it.id != null ? String(it.id) : '';
      return codes.has(code) || (id && ids.has(id));
    });
  }

  private clearOtherSelections(except: ActorKey, s: ContextState): void {
    for (const k of this.keys) {
      if (k !== except && s[k].selected) {
        s[k] = { ...s[k], selected: null };
        sessionStorage.removeItem(this.storageKey(k));
      }
    }
  }

  private normalizeSingleSelection(): void {
    const s = this._state.value;
    let kept: ActorKey | null = null;
    for (const k of this.order) {
      if (s[k].selected) {
        if (!kept) kept = k;
        else {
          s[k] = { ...s[k], selected: null };
          sessionStorage.removeItem(this.storageKey(k));
        }
      }
    }
    this._state.next({ ...s });
  }

  private splitAttr(v: string): string[] {
    try {
      const parsed = JSON.parse(v);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch { /* not JSON */ }
    return v.split(/[;,\s]+/).filter(Boolean);
  }

  clear(opts?: { keepAvailable?: boolean; keys?: ActorKey[] }): void {
    const keepAvailable = !!opts?.keepAvailable;
    const keys = opts?.keys ?? this.keys;
    const s = this.snapshot();
    for (const k of keys) {
      sessionStorage.removeItem(this.storageKey(k));
      s[k] = { ...s[k], selected: null, items: keepAvailable ? s[k].items : [] };
    }
    this._state.next({ ...s });
  }

  clearSelections(keys?: ActorKey[]): void {
    this.clear({ keepAvailable: true, keys });
  }

  selectedEntry(order: ActorKey[] = this.order): { key: ActorKey; actor: Actor } | null {
    const s = this.snapshot();
    for (const k of order) {
      const a = s[k].selected;
      if (a) return { key: k, actor: a };
    }
    return null;
  }

  readonly selectedEntry$ = this.state$.pipe(
    map((s) => {
      for (const k of this.order) {
        const a = s[k].selected;
        if (a) return { key: k, actor: a } as const;
      }
      return null;
    }),
    distinctUntilChanged((a, b) => (a?.key === b?.key) && (a?.actor?.code === b?.actor?.code))
  );
}
